import { Box, RadioGroup } from '@material-ui/core';

import Buttons from './Buttons';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { HelpOutline } from '@material-ui/icons';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Radio from '@material-ui/core/Radio';
import React from 'react';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import { styles } from './ConfirmationDialog';
import { useState } from 'react';
import { withStyles } from '@material-ui/core/styles';

const RESET_OPTIONS = {
    everything: 'everything',
    web: 'web-mobile',
    desktop: 'desktop-mobile',
};

const DEFAULT_RESET_OPTION = RESET_OPTIONS.web;

function ResetPasswordOptions({
    onChange,
    defaultOption = DEFAULT_RESET_OPTION,
}) {
    const radioStyles = { marginLeft: 8 };

    const onChangeCallBack = event => {
        const { value } = event.target;
        onChange({ reset_option: value });
    };

    const options = [
        {
            value: RESET_OPTIONS.web,
            label: 'Web & Mobile',
            tooltip:
                'This option removes Passwords, Shares, Emergency Access, and other encrypted content that users have created and stored in the Web App or Mobile applications since Web App access was enabled. If a user has signed up for the Web App or completed their migration to the Web App, this action could delete all of their content. Please note that deleting user content does not affect their settings or any content that has been shared with them.',
        },
        {
            value: RESET_OPTIONS.desktop,
            label: 'Desktop & Mobile',
            tooltip:
                'This option deletes Passwords, Shares, Emergency Access, and other encrypted content that users created and stored in Desktop or Mobile applications before the Web App launch. If a user has never signed up for or accessed the Web App, this action could delete all of their content.',
        },
        {
            value: RESET_OPTIONS.everything,
            label: 'Everything',
            tooltip:
                "This option deletes all Passwords, Shares, Emergency Access, and other encrypted content from all platforms, regardless of a user's Web App access—whether they have signed up for Web App access or completed their migration to the Web App.",
        },
    ];

    return (
        <div>
            Select how you want to reset the selected accounts:
            <RadioGroup
                name="reset-password-options"
                onChange={onChangeCallBack}
                defaultValue={defaultOption}
            >
                {options.map(option => (
                    <FormControlLabel
                        key={option.value}
                        value={option.value}
                        control={<Radio />}
                        label={
                            <Box display="flex" alignItems="center">
                                {option.label}
                                <Tooltip
                                    title={option.tooltip}
                                    style={radioStyles}
                                    placement="right-end"
                                >
                                    <HelpOutline />
                                </Tooltip>
                            </Box>
                        }
                    />
                ))}
            </RadioGroup>
        </div>
    );
}

const ResetPasswordDialog = props => {
    const [text, setText] = useState('');
    const [options, setOptions] = useState({
        reset_option: DEFAULT_RESET_OPTION,
    });

    const { classes, open, onCancel, onOk, data, displayResetOptions } = props;

    const canOk = text.toLocaleLowerCase() === 'yes';

    const onOkCallback = () => {
        onOk(options);
    };

    return (
        <Dialog open={open} onClose={onCancel} fullWidth={true} maxWidth="sm">
            <DialogTitle>Reset Master Password</DialogTitle>
            <DialogContent>
                <DialogContentText className={classes.subtitle1}>
                    Resetting the master password for an account will also:
                    <ul style={{ marginTop: 5, marginBottom: 0 }}>
                        <li>
                            Delete all passwords and notes saved in the account
                        </li>
                        <li>
                            Cancel any shares where the account is the only
                            Owner of a share
                        </li>
                        <li>
                            Remove personal user backups, unless the account key
                            PDF has been downloaded in advance
                        </li>
                    </ul>
                </DialogContentText>

                <Typography
                    component="div"
                    variant="subtitle1"
                    color="textSecondary"
                    className={classes.subtitle1}
                >
                    Are you sure you want to reset the passwords for these
                    accounts:
                </Typography>
                <div className={classes.data}>
                    <List dense={true}>
                        {data &&
                            data.map((item, index) => (
                                <ListItem key={index} className={classes.dense}>
                                    <ListItemText
                                        className={classes.textDense}
                                        primary={item}
                                    />
                                </ListItem>
                            ))}
                    </List>
                </div>
                {displayResetOptions && (
                    <ResetPasswordOptions onChange={setOptions} />
                )}

                <div className={classes.confirmation}>
                    <FormControl component="fieldset">
                        <FormControlLabel
                            className={classes.labelControl}
                            classes={{
                                label: classes.label,
                            }}
                            control={
                                <TextField
                                    className={classes.textField}
                                    value={text}
                                    onChange={event =>
                                        setText(event.target.value)
                                    }
                                    margin="dense"
                                    variant="outlined"
                                />
                            }
                            label="Type YES to confirm"
                            labelPlacement="start"
                        />
                    </FormControl>
                </div>
            </DialogContent>
            <DialogActions>
                <Buttons>
                    <Buttons.Secondary onClick={onCancel} label="Cancel" />
                    <Buttons.Primary
                        onClick={onOkCallback}
                        autoFocus
                        variant="contained"
                        disabled={!canOk}
                        label="Reset Account(s)"
                    />
                </Buttons>
            </DialogActions>
        </Dialog>
    );
};

export default withStyles(styles)(ResetPasswordDialog);
